// Auth state for the dashboard. One composable, one fetch on mount.
//
// Usage:
//   import { useAuth } from '@/composables/useAuth'
//   const { user, loading, refresh, signIn, signInWithEmail,
//           signUpWithEmail, signOut } = useAuth()
//
// The user object is the response from /api/auth/me:
//   { workspace_id, email, display_name, avatar_url, tier, is_paid,
//     has_google, has_password, email_verified, cookie_consent_analytics }
//
// signIn() opens the Google OAuth flow in the same window. The
// backend sets a `prufa_session` cookie on callback; subsequent
// fetches carry the cookie via `credentials: 'include'`.

import { ref } from 'vue'
import { initAnalytics, setAnalyticsUser } from '@/utils/analytics.js'

const user = ref(null)
const loading = ref(false)
const error = ref('')
let refreshPromise = null

async function _fetchMe() {
  const r = await fetch('/api/auth/me', { credentials: 'include' })
  if (r.status === 401) return null
  if (!r.ok) throw new Error(`auth me failed: HTTP ${r.status}`)
  return r.json()
}

async function _readError(r) {
  let body = null
  try {
    body = await r.json()
  } catch {
    // fall through
  }
  if (body && body.detail) {
    if (typeof body.detail === 'object' && body.detail.code) {
      return { code: body.detail.code, message: body.detail.hint || body.detail.code }
    }
    return { code: 'unknown', message: String(body.detail) }
  }
  return { code: 'unknown', message: `HTTP ${r.status}` }
}

export function useAuth() {
  async function refresh() {
    if (refreshPromise) return refreshPromise
    refreshPromise = (async () => {
      loading.value = true
      error.value = ''
      try {
        user.value = await _fetchMe()
        setAnalyticsUser(user.value)
        await initAnalytics(user.value?.cookie_consent_analytics ?? null)
      } catch (e) {
        error.value = String(e)
        user.value = null
        setAnalyticsUser(null)
        await initAnalytics(null)
      } finally {
        loading.value = false
        refreshPromise = null
      }
    })()
    return refreshPromise
  }

  function signIn(next = '/') {
    const params = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''
    window.location.href = `/api/auth/google/login${params}`
  }

  async function _postAuth(path, body, { headers: extraHeaders = {} } = {}) {
    const headers = { 'Content-Type': 'application/json', ...extraHeaders }
    const r = await fetch(path, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    })
    if (r.ok) {
      const data = await r.json()
      user.value = data
      setAnalyticsUser(data)
      return { ok: true, data }
    }
    return { ok: false, ...(await _readError(r)) }
  }

  async function signInWithEmail({ email, password }) {
    return _postAuth('/api/auth/email/login', { email, password })
  }

  async function signUpWithEmail({ email, password, displayName }) {
    return _postAuth('/api/auth/email/signup', {
      email,
      password,
      display_name: displayName,
    })
  }

  async function setPassword({ password, idempotencyKey }) {
    // The session cookie is HttpOnly; fetch the real session-bound token.
    // Keep it local to this operation, out of user state and analytics.
    const r = await fetch('/api/auth/csrf', { credentials: 'include', cache: 'no-store' })
    if (!r.ok) return { ok: false, ...(await _readError(r)) }
    const { csrf_token: csrfToken } = await r.json()
    if (!csrfToken) throw new Error('Session token unavailable. Refresh and try again.')
    return _postAuth('/api/auth/account/password', { password }, {
      headers: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': idempotencyKey },
    })
  }

  async function requestPasswordReset({ email }) {
    const r = await fetch('/api/auth/email/reset/request', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    // Always 200 — the endpoint is enumeration-safe.
    return r.ok
  }

  async function resendVerification() {
    const r = await fetch('/api/auth/email/verify/resend', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': getCookie('prufa_session') ? '1' : '' },
    })
    if (r.status === 401) return { ok: false, code: 'auth_required' }
    if (r.ok) return { ok: true, ...(await r.json()) }
    return { ok: false, ...(await _readError(r)) }
  }

  async function signOut() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCookie('prufa_session') ? '1' : '' },
      })
    } catch {
      // Best-effort — even if the request fails, the user is signing
      // out from this client. The server-side session may linger
      // until it expires (30d), but no further calls succeed.
    }
    user.value = null
    setAnalyticsUser(null)
  }

  async function updateAnalyticsConsent(allowed) {
    const r = await fetch('/api/auth/account/analytics-consent', {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCookie('prufa_session') ? '1' : '',
      },
      body: JSON.stringify({ cookie_consent_analytics: allowed }),
    })
    const body = await r.json().catch(() => ({}))
    if (!r.ok) {
      throw new Error(body.detail?.hint || body.detail?.code || `analytics consent failed: HTTP ${r.status}`)
    }
    user.value = body
    setAnalyticsUser(body)
    await initAnalytics(body.cookie_consent_analytics)
    return body
  }

  return {
    user,
    loading,
    error,
    refresh,
    signIn,
    signInWithEmail,
    signUpWithEmail,
    setPassword,
    requestPasswordReset,
    resendVerification,
    signOut,
    updateAnalyticsConsent,
  }
}

function getCookie(name) {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[2]) : null
}
